import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Reads a `?focus=<ID>` query param on arrival and exposes it as a transient
 * highlight id that auto-clears. Deep-link chips from Decca carry this param so
 * the target page can briefly pulse the row/card the AI changed or referenced.
 *
 * Unknown ids are not this hook's concern — the consuming page no-ops when the
 * element doesn't exist.
 *
 * @param {object} [opts]
 * @param {number} [opts.duration=1400] - How long (ms) the id stays "focused".
 * @returns {string} The focus id, or "" when none / after it clears.
 */
const useFocusHighlight = ({ duration = 1400 } = {}) => {
  const location = useLocation();
  const [focusId, setFocusId] = useState("");

  useEffect(() => {
    const id = new URLSearchParams(location.search).get("focus");
    if (!id) {
      setFocusId("");
      return undefined;
    }
    setFocusId(id);
    const timer = setTimeout(() => setFocusId(""), duration);
    return () => clearTimeout(timer);
  }, [location.search, duration]);

  return focusId;
};

export default useFocusHighlight;
